import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { StaticMap } from "@freightsimple/ui-core/Display/StaticMap";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import {
  inputWidthPx,
  type InputWidth,
} from "@freightsimple/ui-core/Controls/inputWidth";
import SlideDown from "@/Shared/Animations/CustomComponents/SlideDown";
import { formatPostalCode } from "@/Helpers/formatPostalCode";
import type { LatitudeLongitude } from "@freightsimple/generated-dashboard-openapi-client/models/LatitudeLongitude";
import { CountryCode } from "@freightsimple/generated-dashboard-openapi-client/models/CountryCode";
import type { ReactNode } from "react";
import LocationsPostalCodeCityDropdown from "../Dropdowns/LocationsPostalCodeCityDropdown";
import { LocationsFlag } from "../CustomComponents/LocationsFlag";

export interface LocationsPostalCodeFieldProps {
  postalCode: string;
  onUpdate: (postalCode: string) => void;
  city: string;
  cities: string[] | undefined;
  onCitySelected: (city: string) => void;
  stateOrProvinceCode: string;
  countryCode: CountryCode | undefined;
  loading: boolean;
  label?: ReactNode;
  errorMessage: ErrorMessageType;
  warningMessage: ErrorMessageType;
  forceValidation: boolean;
  width?: InputWidth | undefined;
  displayName: string | undefined;
  enabled: boolean;
  showMap: boolean;
  latitudeLongitude: LatitudeLongitude | undefined;
  mapLoading: boolean;
  lookupUnavailable: boolean;
  onRetryLookup: () => void;
}

/**
 * What the postal code field looks like for a given lookup state. The lookup
 * itself lives in LocationsPostalCodeInput; this only renders its result.
 */
const LocationsPostalCodeField: React.FC<LocationsPostalCodeFieldProps> = (
  props: LocationsPostalCodeFieldProps,
) => {
  function renderCityPill() {
    if (!props.stateOrProvinceCode) {
      return undefined;
    }

    if (props.cities !== undefined && props.cities.length > 1) {
      return (
        <LocationsPostalCodeCityDropdown
          cities={props.cities}
          selectedCity={props.city}
          stateOrProvinceCode={props.stateOrProvinceCode}
          countryCode={props.countryCode}
          onCitySelected={props.onCitySelected}
        />
      );
    }

    // The adornment slot is already a centred flex row, so the label and the
    // flag go straight into it. `nowrap` on the label keeps "City, ST" from
    // breaking in that narrow slot and pushing the flag onto its own line.
    return (
      <>
        <T
          color="muted"
          nowrap
        >{`${props.city}, ${props.stateOrProvinceCode}`}</T>
        <LocationsFlag countryCode={props.countryCode} />
      </>
    );
  }

  function onChange(value: string) {
    // Remove leading spaces and non-alphanumeric characters from postal code
    const trimmedValue = value.replace(/^\s+/, "").trim();
    const alphanumericOnly = trimmedValue.replace(/[^A-Za-z0-9]/g, "");
    const postalCode = formatPostalCode(alphanumericOnly);
    props.onUpdate(postalCode);
  }

  const latitude = props.latitudeLongitude?.latitude;
  const longitude = props.latitudeLongitude?.longitude;
  const hasCoordinates =
    latitude !== undefined && longitude !== undefined && !!props.city;

  // A fixed-width field gets a map pinned to the same token, so the two edges
  // line up. `full` has no pixel width, and needs none — the field and the map
  // both fill the column, which has a width of its own.
  const mapWidth = props.width ? inputWidthPx[props.width] : undefined;

  // Our postal code providers could not answer, so the code was never checked.
  // Saying "not found" would tell the customer a valid code is wrong. The same
  // goes while a lookup is running: the form reads "no city yet" as not found.
  const errorMessage = props.loading ? undefined : props.lookupUnavailable ? (
    <>
      We couldn't check this postal code right now.{" "}
      <Button
        size="link"
        inheritFont
        onClick={props.onRetryLookup}
        data-testid="postal-code-retry-lookup-button"
      >
        Try again
      </Button>
    </>
  ) : (
    props.errorMessage
  );

  return (
    <ShowFormErrors show={props.forceValidation || props.lookupUnavailable}>
      <FormItem
        label={props.label}
        error={errorMessage}
        warning={props.warningMessage}
      >
        <Stack gap={2}>
          <TextInput
            value={props.postalCode}
            onChange={(e) => onChange(e.target.value)}
            endAdornment={renderCityPill()}
            loading={props.loading}
            width={props.width}
            disabled={!props.enabled}
            placeholder={
              props.displayName
                ? `Enter ${props.displayName} Postal Code`
                : `Enter Postal Code`
            }
            data-testid="postal-code-input"
          />
          <SlideDown show={props.showMap && hasCoordinates}>
            {latitude !== undefined && longitude !== undefined && (
              <StaticMap
                latitude={latitude}
                longitude={longitude}
                zoom={11}
                height={156}
                {...(mapWidth !== undefined && { width: mapWidth })}
                loading={props.mapLoading}
                href={`http://maps.apple.com/?ll=${latitude},${longitude}&q=${props.city},+${props.stateOrProvinceCode},+${props.postalCode}`}
              />
            )}
          </SlideDown>
        </Stack>
      </FormItem>
    </ShowFormErrors>
  );
};

export default LocationsPostalCodeField;
